import { Protocol } from '@uniswap/router-sdk'
import { Token, TradeType } from '@uniswap/sdk-core'
import {
  AlphaRouterConfig,
  ITokenListProvider,
  IV3SubgraphProvider,
  parseFeeAmount,
  unparseFeeAmount,
  V3SubgraphPool,
} from '@uniswap/smart-order-router'
import { FeeAmount } from '@uniswap/v3-sdk'
import { SupportedChainId } from 'constants/chains'
import { WRAPPED_NATIVE_CURRENCY } from 'constants/tokens'
import arbTokens from 'constants/tokens/networks/arbitrum'
import fantomTokens from 'constants/tokens/networks/fantom'
import zkTokens from 'constants/tokens/networks/zk-sync'
import _ from 'lodash'

import { V3PoolAccessor, V3PoolProvider } from '../providers/pool-provider'
import { TokenProvider } from '../providers/token-provider'

export type PoolId = { id: string }
export type CandidatePoolsBySelectionCriteria = {
  protocol: Protocol
  selections: CandidatePoolsSelections
}

/// Utility type for allowing us to use `keyof CandidatePoolsSelections` to map
export type CandidatePoolsSelections = {
  topByBaseWithTokenIn: PoolId[]
  topByBaseWithTokenOut: PoolId[]
  topByDirectSwapPool: PoolId[]
  topByEthQuoteTokenPool: PoolId[]
  topByTVL: PoolId[]
  topByTVLUsingTokenIn: PoolId[]
  topByTVLUsingTokenOut: PoolId[]
  topByTVLUsingTokenInSecondHops: PoolId[]
  topByTVLUsingTokenOutSecondHops: PoolId[]
}

export type V3GetCandidatePoolsParams = {
  tokenIn: Token
  tokenOut: Token
  routeType: TradeType
  routingConfig: AlphaRouterConfig
  subgraphProvider: IV3SubgraphProvider
  tokenProvider: TokenProvider
  poolProvider: V3PoolProvider
  blockedTokenListProvider?: ITokenListProvider
  chainId: SupportedChainId
}

const baseTokensByChain: { [chainId in SupportedChainId]?: Token[] } = {
  [SupportedChainId.ZK_SYNC]: [zkTokens.usdc, zkTokens.busd, WRAPPED_NATIVE_CURRENCY[SupportedChainId.ZK_SYNC]!],
  [SupportedChainId.FANTOM]: [
    fantomTokens.dai,
    fantomTokens.frax,
    fantomTokens.usdc,
    fantomTokens.usdt,
    WRAPPED_NATIVE_CURRENCY[SupportedChainId.FANTOM]!,
  ],
  [SupportedChainId.ARBITRUM]: [arbTokens.usdc, WRAPPED_NATIVE_CURRENCY[SupportedChainId.ARBITRUM]!],
}

export async function getV3CandidatePools({
  tokenIn,
  tokenOut,
  routeType,
  routingConfig,
  subgraphProvider,
  tokenProvider,
  poolProvider,
  blockedTokenListProvider,
  chainId,
}: V3GetCandidatePoolsParams): Promise<{
  poolAccessor: V3PoolAccessor
  candidatePools: CandidatePoolsBySelectionCriteria
  subgraphPools: V3SubgraphPool[]
}> {
  const {
    blockNumber,
    v3PoolSelection: {
      topN,
      topNDirectSwaps,
      topNTokenInOut,
      topNSecondHop,
      //@ts-ignore
      topNSecondHopForTokenAddress,
      topNWithEachBaseToken,
      topNWithBaseToken,
    },
  } = routingConfig
  const tokenInAddress = tokenIn.address.toLowerCase()
  const tokenOutAddress = tokenOut.address.toLowerCase()

  const allPoolsRaw = await subgraphProvider.getPools(tokenIn, tokenOut, {
    blockNumber,
  })

  const allPools = _.map(allPoolsRaw, (pool) => {
    return {
      ...pool,
      token0: {
        ...pool.token0,
        id: pool.token0.id.toLowerCase(),
      },
      token1: {
        ...pool.token1,
        id: pool.token1.id.toLowerCase(),
      },
    }
  })

  // Only consider pools where neither tokens are in the blocked token list.
  let filteredPools: V3SubgraphPool[] = allPools
  if (blockedTokenListProvider) {
    filteredPools = []
    for (const pool of allPools) {
      const token0InBlocklist = await blockedTokenListProvider.getTokenByAddress(pool.token0.id)
      const token1InBlocklist = await blockedTokenListProvider.getTokenByAddress(pool.token1.id)

      if (token0InBlocklist || token1InBlocklist) {
        continue
      }

      filteredPools.push(pool)
    }
  }

  const subgraphPoolsSorted = _(filteredPools)
    .sortBy((tokenListPool) => -tokenListPool.tvlUSD)
    .value()

  const poolAddressesSoFar = new Set<string>()
  const addToAddressSet = (pools: V3SubgraphPool[]) => {
    _(pools)
      .map((pool) => pool.id)
      .forEach((poolAddress) => poolAddressesSoFar.add(poolAddress))
  }

  const baseTokens = baseTokensByChain[chainId] ?? []

  const topByBaseWithTokenIn = _(baseTokens)
    .flatMap((token: Token) => {
      return _(subgraphPoolsSorted)
        .filter((subgraphPool) => {
          const tokenAddress = token.address.toLowerCase()
          return (
            (subgraphPool.token0.id == tokenAddress && subgraphPool.token1.id == tokenInAddress) ||
            (subgraphPool.token1.id == tokenAddress && subgraphPool.token0.id == tokenInAddress)
          )
        })
        .sortBy((tokenListPool) => -tokenListPool.tvlUSD)
        .slice(0, topNWithEachBaseToken)
        .value()
    })
    .sortBy((tokenListPool) => -tokenListPool.tvlUSD)
    .slice(0, topNWithBaseToken)
    .value()

  const topByBaseWithTokenOut = _(baseTokens)
    .flatMap((token: Token) => {
      return _(subgraphPoolsSorted)
        .filter((subgraphPool) => {
          const tokenAddress = token.address.toLowerCase()
          return (
            (subgraphPool.token0.id == tokenAddress && subgraphPool.token1.id == tokenOutAddress) ||
            (subgraphPool.token1.id == tokenAddress && subgraphPool.token0.id == tokenOutAddress)
          )
        })
        .sortBy((tokenListPool) => -tokenListPool.tvlUSD)
        .slice(0, topNWithEachBaseToken)
        .value()
    })
    .sortBy((tokenListPool) => -tokenListPool.tvlUSD)
    .slice(0, topNWithBaseToken)
    .value()

  let top2DirectSwapPool = _(subgraphPoolsSorted)
    .filter((subgraphPool) => {
      return (
        !poolAddressesSoFar.has(subgraphPool.id) &&
        ((subgraphPool.token0.id == tokenInAddress && subgraphPool.token1.id == tokenOutAddress) ||
          (subgraphPool.token1.id == tokenInAddress && subgraphPool.token0.id == tokenOutAddress))
      )
    })
    .slice(0, topNDirectSwaps)
    .value()

  if (top2DirectSwapPool.length == 0 && topNDirectSwaps > 0) {
    // If we requested direct swap pools but did not find any in the subgraph query.
    // Optimistically add them into the query regardless. Invalid pools ones will be dropped anyway
    // when we query the pool on-chain. Ensures that new pools for new pairs can be swapped on immediately.
    top2DirectSwapPool = _.map([FeeAmount.HIGH, FeeAmount.MEDIUM, FeeAmount.LOW, FeeAmount.LOWEST], (feeAmount) => {
      const { token0, token1, poolAddress } = poolProvider.getPoolAddress(tokenIn, tokenOut, feeAmount)
      return {
        id: poolAddress,
        feeTier: unparseFeeAmount(feeAmount),
        liquidity: '10000',
        token0: {
          id: token0.address,
        },
        token1: {
          id: token1.address,
        },
        tvlETH: 10000,
        tvlUSD: 10000,
      }
    })
  }

  addToAddressSet(top2DirectSwapPool)

  const wrappedNativeAddress = WRAPPED_NATIVE_CURRENCY[chainId]?.address

  // Main reason we need this is for gas estimates, only needed if token out is not native.
  // We don't check the seen address set because if we've already added pools for getting native quotes
  // theres no need to add more.
  let top2EthQuoteTokenPool: V3SubgraphPool[] = []
  if (
    WRAPPED_NATIVE_CURRENCY[chainId]?.symbol == WRAPPED_NATIVE_CURRENCY[SupportedChainId.ZK_SYNC]?.symbol &&
    tokenOut.symbol != 'WETH' &&
    tokenOut.symbol != 'WETH9' &&
    tokenOut.symbol != 'ETH'
  ) {
    top2EthQuoteTokenPool = _(subgraphPoolsSorted)
      .filter((subgraphPool) => {
        if (routeType == TradeType.EXACT_INPUT) {
          return (
            (subgraphPool.token0.id == wrappedNativeAddress && subgraphPool.token1.id == tokenOutAddress) ||
            (subgraphPool.token1.id == wrappedNativeAddress && subgraphPool.token0.id == tokenOutAddress)
          )
        } else {
          return (
            (subgraphPool.token0.id == wrappedNativeAddress && subgraphPool.token1.id == tokenInAddress) ||
            (subgraphPool.token1.id == wrappedNativeAddress && subgraphPool.token0.id == tokenInAddress)
          )
        }
      })
      .slice(0, 1)
      .value()
  }

  addToAddressSet(top2EthQuoteTokenPool)

  const topByTVL = _(subgraphPoolsSorted)
    .filter((subgraphPool) => {
      return !poolAddressesSoFar.has(subgraphPool.id)
    })
    .slice(0, topN)
    .value()

  addToAddressSet(topByTVL)

  const topByTVLUsingTokenIn = _(subgraphPoolsSorted)
    .filter((subgraphPool) => {
      return (
        !poolAddressesSoFar.has(subgraphPool.id) &&
        (subgraphPool.token0.id == tokenInAddress || subgraphPool.token1.id == tokenInAddress)
      )
    })
    .slice(0, topNTokenInOut)
    .value()

  addToAddressSet(topByTVLUsingTokenIn)

  const topByTVLUsingTokenOut = _(subgraphPoolsSorted)
    .filter((subgraphPool) => {
      return (
        !poolAddressesSoFar.has(subgraphPool.id) &&
        (subgraphPool.token0.id == tokenOutAddress || subgraphPool.token1.id == tokenOutAddress)
      )
    })
    .slice(0, topNTokenInOut)
    .value()

  addToAddressSet(topByTVLUsingTokenOut)

  const topByTVLUsingTokenInSecondHops = _(topByTVLUsingTokenIn)
    .map((subgraphPool) => {
      return tokenInAddress == subgraphPool.token0.id ? subgraphPool.token1.id : subgraphPool.token0.id
    })
    .flatMap((secondHopId: string) => {
      return _(subgraphPoolsSorted)
        .filter((subgraphPool) => {
          return (
            !poolAddressesSoFar.has(subgraphPool.id) &&
            (subgraphPool.token0.id == secondHopId || subgraphPool.token1.id == secondHopId)
          )
        })
        .slice(0, topNSecondHopForTokenAddress?.get(secondHopId) ?? topNSecondHop)
        .value()
    })
    .uniqBy((pool) => pool.id)
    .value()

  addToAddressSet(topByTVLUsingTokenInSecondHops)

  const topByTVLUsingTokenOutSecondHops = _(topByTVLUsingTokenOut)
    .map((subgraphPool) => {
      return tokenOutAddress == subgraphPool.token0.id ? subgraphPool.token1.id : subgraphPool.token0.id
    })
    .flatMap((secondHopId: string) => {
      return _(subgraphPoolsSorted)
        .filter((subgraphPool) => {
          return (
            !poolAddressesSoFar.has(subgraphPool.id) &&
            (subgraphPool.token0.id == secondHopId || subgraphPool.token1.id == secondHopId)
          )
        })
        .slice(0, topNSecondHopForTokenAddress?.get(secondHopId) ?? topNSecondHop)
        .value()
    })
    .uniqBy((pool) => pool.id)
    .value()

  addToAddressSet(topByTVLUsingTokenOutSecondHops)

  const subgraphPools = _([
    ...topByBaseWithTokenIn,
    ...topByBaseWithTokenOut,
    ...top2DirectSwapPool,
    ...top2EthQuoteTokenPool,
    ...topByTVL,
    ...topByTVLUsingTokenIn,
    ...topByTVLUsingTokenOut,
    ...topByTVLUsingTokenInSecondHops,
    ...topByTVLUsingTokenOutSecondHops,
  ])
    .compact()
    .uniqBy((pool) => pool.id)
    .value()

  const tokenAddresses = _(subgraphPools)
    .flatMap((subgraphPool) => [subgraphPool.token0.id, subgraphPool.token1.id])
    .compact()
    .uniq()
    .value()

  const tokenAccessor = await tokenProvider.getTokens(tokenAddresses, {
    blockNumber,
  })

  const tokenPairsRaw = _.map<V3SubgraphPool, [Token, Token, FeeAmount] | undefined>(subgraphPools, (subgraphPool) => {
    const tokenA = tokenAccessor.getTokenByAddress(subgraphPool.token0.id)
    const tokenB = tokenAccessor.getTokenByAddress(subgraphPool.token1.id)
    let fee: FeeAmount
    try {
      fee = parseFeeAmount(subgraphPool.feeTier)
    } catch (err) {
      return undefined
    }

    if (!tokenA || !tokenB) {
      return undefined
    }

    return [tokenA, tokenB, fee]
  })

  const tokenPairs = _.compact(tokenPairsRaw)

  const poolAccessor = await poolProvider.getPools(tokenPairs)

  const poolsBySelection: CandidatePoolsBySelectionCriteria = {
    protocol: Protocol.V3,
    selections: {
      topByBaseWithTokenIn,
      topByBaseWithTokenOut,
      topByDirectSwapPool: top2DirectSwapPool,
      topByEthQuoteTokenPool: top2EthQuoteTokenPool,
      topByTVL,
      topByTVLUsingTokenIn,
      topByTVLUsingTokenOut,
      topByTVLUsingTokenInSecondHops,
      topByTVLUsingTokenOutSecondHops,
    },
  }

  return { poolAccessor, candidatePools: poolsBySelection, subgraphPools }
}
